"use client";

import * as React from "react";
import type { AccentTheme } from "@/lib/site-content";
import { useAnalytics } from "@/lib/analytics";

const STORAGE_KEY = "zapify-theme-color";
const DEFAULT_THEME: AccentTheme = "yellow";

const LEGACY_ACCENT: Record<string, AccentTheme> = {
  violet: "blue",
  cyan: "blue",
  rose: "red",
};

function normalizeStored(raw: string | null): AccentTheme {
  if (!raw) return DEFAULT_THEME;
  const valid: AccentTheme[] = ["zapify", "blue", "orange", "green", "yellow", "red"];
  if (valid.includes(raw as AccentTheme)) return raw as AccentTheme;
  return LEGACY_ACCENT[raw] ?? DEFAULT_THEME;
}

type ColorThemeContextValue = {
  themeColor: AccentTheme;
  /** Persist accent to storage and React state. */
  setThemeColor: (theme: AccentTheme, source?: string) => void;
  /** Temporarily show an accent on the root element only; pass `null` to restore the last committed accent. */
  previewThemeColor: (theme: AccentTheme | null) => void;
};

const ColorThemeContext = React.createContext<ColorThemeContextValue | null>(null);

export function ColorThemeProvider({ children }: { children: React.ReactNode }) {
  const { track } = useAnalytics();
  const [themeColor, setThemeColorState] = React.useState<AccentTheme>(DEFAULT_THEME);
  const committedRef = React.useRef<AccentTheme>(DEFAULT_THEME);

  React.useEffect(() => {
    const resolved = normalizeStored(window.localStorage.getItem(STORAGE_KEY));
    committedRef.current = resolved;
    document.documentElement.dataset.themeColor = resolved;
    // Apply stored accent after mount so server HTML matches the default and avoids hydration mismatch.
    // eslint-disable-next-line react-hooks/set-state-in-effect -- client-only read from localStorage
    setThemeColorState(resolved);
  }, []);

  const setThemeColor = React.useCallback((theme: AccentTheme, source = "unknown_color_switch") => {
    const previousTheme = committedRef.current;

    committedRef.current = theme;
    setThemeColorState(theme);
    document.documentElement.dataset.themeColor = theme;
    window.localStorage.setItem(STORAGE_KEY, theme);
    track("accent_color_changed", {
      accent_color: theme,
      previous_accent_color: previousTheme,
      prompt: `accent-color-${theme}`,
      prompt_variant: theme,
      source,
      section: source.includes("footer") ? "footer" : "navbar",
    });
  }, [track]);

  const previewThemeColor = React.useCallback((theme: AccentTheme | null) => {
    document.documentElement.dataset.themeColor = theme ?? committedRef.current;
  }, []);

  return (
    <ColorThemeContext.Provider value={{ themeColor, setThemeColor, previewThemeColor }}>
      {children}
    </ColorThemeContext.Provider>
  );
}

export function useColorTheme() {
  const context = React.useContext(ColorThemeContext);
  if (!context) {
    throw new Error("useColorTheme must be used within ColorThemeProvider");
  }
  return context;
}
